import { computed, ref } from 'vue';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  QTool,
  isConnectedMode,
  type QToolDevice,
  type QToolMode,
} from '@/native/plugins/QTool';
import { useDmgw6Store } from '@/store/dmgw6/dmgw6Store';

export type QToolConnectionPhase =
  | { phase: 'idle' }
  | { phase: 'scanning'; devices: QToolDevice[] }
  | { phase: 'connecting'; device: QToolDevice }
  | { phase: 'connected'; device: QToolDevice }
  | {
      phase: 'disconnected';
      lastDevice: QToolDevice;
      reason: 'timeout' | 'error' | 'user';
    }
  | { phase: 'error'; device?: QToolDevice; message: string };

const state = ref<QToolConnectionPhase>({ phase: 'idle' });
let currentDevice: QToolDevice | null = null;
let listenersInitialized = false;

function applyMode(mode: QToolMode) {
  if (mode.type === 'connecting') {
    const device = mode.qtool ?? currentDevice;
    if (device) {
      currentDevice = device;
      state.value = { phase: 'connecting', device };
    }
    return;
  }

  if (isConnectedMode(mode) && 'qtool' in mode) {
    const device = mode.qtool;
    currentDevice = device;
    state.value = { phase: 'connected', device };
    useDmgw6Store().setConnectedQtool(device);
    return;
  }

  // mode is 'idle' or 'qtoolDiscovery' — not connected.
  const wasConnecting = state.value.phase === 'connecting';
  const wasConnected = state.value.phase === 'connected';
  const lastDevice = currentDevice;

  if (wasConnecting && lastDevice) {
    state.value = {
      phase: 'error',
      device: lastDevice,
      message: 'Connection failed',
    };
  } else if (wasConnected && lastDevice) {
    state.value = {
      phase: 'disconnected',
      lastDevice,
      reason: 'timeout',
    };
  }

  if (wasConnecting || wasConnected) {
    currentDevice = null;
    useDmgw6Store().clearConnectedQtool();
  }
}

function setupListeners() {
  if (listenersInitialized) return;
  listenersInitialized = true;

  const storedQtool = useDmgw6Store().connectedQtool;
  if (storedQtool) {
    state.value = { phase: 'connected', device: storedQtool };
    currentDevice = storedQtool;
  }

  QTool.addListener('qtoolDiscoveryResult', (data) => {
    if (state.value.phase === 'scanning') {
      state.value = { phase: 'scanning', devices: data.qtools };
    }
  });

  QTool.addListener('qtoolDiscoveryStopped', () => {
    if (state.value.phase === 'scanning') {
      state.value = { phase: 'idle' };
    }
  });

  QTool.addListener('qtoolModeChanged', applyMode);

  if (Capacitor.isNativePlatform()) {
    CapacitorApp.addListener('resume', () => {
      QTool.syncMode();
    });
  }
}

export function useQToolConnection() {
  setupListeners();

  const isConnected = computed(() => state.value.phase === 'connected');
  const connectedDevice = computed(() =>
    'device' in state.value ? state.value.device : null
  );

  async function scan() {
    state.value = { phase: 'scanning', devices: [] };
    await QTool.startQToolDiscovery();
  }

  async function stopScan() {
    if (state.value.phase !== 'scanning') return;
    await QTool.stopQToolDiscovery();
    state.value = { phase: 'idle' };
  }

  async function connect(device: QToolDevice) {
    currentDevice = device;
    state.value = { phase: 'connecting', device };
    await QTool.connect({ address: device.address });
  }

  async function disconnect() {
    const device = currentDevice;
    currentDevice = null;
    useDmgw6Store().clearConnectedQtool();
    await QTool.disconnect();
    if (device) {
      state.value = {
        phase: 'disconnected',
        lastDevice: device,
        reason: 'user',
      };
    } else {
      state.value = { phase: 'idle' };
    }
  }

  function reset() {
    currentDevice = null;
    state.value = { phase: 'idle' };
  }

  return {
    state,
    isConnected,
    connectedDevice,
    scan,
    stopScan,
    connect,
    disconnect,
    reset,
  };
}
